import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { marketingUrl } from "@/lib/marketingUrl";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  readGranularConsent,
  writeGranularConsent,
  CONSENT_STORAGE_KEY_V2,
  type GranularConsent,
} from "@/lib/tracking/consent";

/** @deprecated use readGranularConsent */
export const COOKIE_CONSENT_STORAGE_KEY = "fastsell_cookie_consent_v1";

export type CookieConsentValue = "accepted" | "rejected";

/** @deprecated */
export function readCookieConsent(): CookieConsentValue | null {
  const g = readGranularConsent();
  if (!g) return null;
  if (g.analytics && g.marketing) return "accepted";
  if (!g.analytics && !g.marketing) return "rejected";
  return null;
}

export function CookieConsentBanner() {
  const [open, setOpen] = useState(() => readGranularConsent() === null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [draft, setDraft] = useState<GranularConsent>({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === CONSENT_STORAGE_KEY_V2 || e.key === COOKIE_CONSENT_STORAGE_KEY) {
        setOpen(readGranularConsent() === null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const commit = (value: GranularConsent) => {
    writeGranularConsent(value);
    setOpen(false);
    setPrefsOpen(false);
  };

  const acceptAll = () => commit({ analytics: true, marketing: true });
  const rejectNonEssential = () => commit({ analytics: false, marketing: false });

  const savePrefs = () => commit(draft);

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-dv-header border-t border-border/80 bg-background/95 p-4 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] backdrop-blur-md dark:shadow-[0_-8px_30px_rgba(0,0,0,0.35)]"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <div className="flex max-w-4xl flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="text-sm leading-relaxed text-muted-foreground">
            <p id="cookie-banner-title" className="font-semibold text-foreground">
              Cookies et traceurs
            </p>
            <p className="mt-1">
              Nous utilisons des cookies nécessaires au fonctionnement du service. Vous pouvez accepter ou refuser les
              mesures d’audience et le marketing (ex. Meta Pixel). Consultez notre{" "}
              <a
                href={marketingUrl("/legal/cookies")}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Politique relative aux cookies
              </a>{" "}
              et notre{" "}
              <a
                href={marketingUrl("/legal/privacy")}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Politique de confidentialité
              </a>
              .
            </p>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2 sm:justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => setPrefsOpen((v) => !v)}>
              Préférences
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={rejectNonEssential} className="min-w-[88px]">
              Tout refuser
            </Button>
            <Button type="button" size="sm" onClick={acceptAll} className="min-w-[88px]">
              Tout accepter
            </Button>
          </div>
        </div>

        {prefsOpen && (
          <div className="rounded-lg border border-border/80 bg-muted/30 p-4 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="consent-analytics" className="text-foreground">
                  Mesure d’audience
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Statistiques agrégées sur la boutique (événements internes FastSell).
                </p>
              </div>
              <Switch
                id="consent-analytics"
                checked={draft.analytics}
                onCheckedChange={(c) => setDraft((d) => ({ ...d, analytics: c }))}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="consent-marketing" className="text-foreground">
                  Marketing & publicité
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Pixels tiers (ex. Meta) pour la mesure publicitaire — uniquement si activé par le marchand.
                </p>
              </div>
              <Switch
                id="consent-marketing"
                checked={draft.marketing}
                onCheckedChange={(c) => setDraft((d) => ({ ...d, marketing: c }))}
              />
            </div>
            <Button type="button" size="sm" onClick={savePrefs}>
              Enregistrer mes choix
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
